import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase/config';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import {
  POINTS_ACTIONS,
  REWARD_CATALOG,
  calculateLevel,
  localizeReward,
  localizeBadge
} from '../constants/rewardsConfig';

const MarketingContext = createContext();

export { POINTS_ACTIONS };

export const useMarketing = () => {
  const context = useContext(MarketingContext);
  if (!context) {
    throw new Error('useMarketing must be used within a MarketingProvider');
  }
  return context;
};

const ensureRewardsProfileFn = httpsCallable(functions, 'ensureRewardsProfile');
const awardPointsFn = httpsCallable(functions, 'awardPoints');
const claimReferralFn = httpsCallable(functions, 'claimReferral');
const redeemRewardFn = httpsCallable(functions, 'redeemReward');
const getMyRewardsDashboardFn = httpsCallable(functions, 'getMyRewardsDashboard');

/**
 * Récompenses CEP — lecture client, mutations via Cloud Functions.
 */
export const MarketingProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [userPoints, setUserPoints] = useState(0);
  const [userBadges, setUserBadges] = useState([]);
  const [referralCode, setReferralCode] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marketingStats, setMarketingStats] = useState({
    totalReferrals: 0,
    activeReferrals: 0,
    totalPoints: 0,
    level: 1
  });

  const applyProfile = useCallback((data) => {
    const points = Number(data?.points || 0);
    setUserPoints(points);
    setUserBadges(Array.isArray(data?.badges) ? data.badges : []);
    setReferralCode(data?.referralCode || null);
    setMarketingStats({
      totalReferrals: Number(data?.totalReferrals || 0),
      activeReferrals: Number(data?.totalReferrals || 0),
      totalPoints: points,
      level: Number(data?.level) || calculateLevel(points)
    });
    if (Array.isArray(data?.history)) setPointsHistory(data.history);
    if (Array.isArray(data?.coupons)) setCoupons(data.coupons);
  }, []);

  const loadMarketingData = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const { data } = await getMyRewardsDashboardFn({});
      applyProfile(data);
      setReferrals([]);
    } catch (error) {
      console.warn('[rewards] dashboard fallback ensure', error);
      try {
        const { data } = await ensureRewardsProfileFn({});
        applyProfile(data);
      } catch (err) {
        console.error('[rewards] ensure profile', err);
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser, applyProfile]);

  useEffect(() => {
    if (currentUser) {
      loadMarketingData();
    } else {
      setUserPoints(0);
      setUserBadges([]);
      setReferralCode(null);
      setPointsHistory([]);
      setCoupons([]);
      setMarketingStats({
        totalReferrals: 0,
        activeReferrals: 0,
        totalPoints: 0,
        level: 1
      });
      setLoading(false);
    }
  }, [currentUser, loadMarketingData]);

  const registerReferral = async (referralCodeUsed) => {
    if (!currentUser || !referralCodeUsed) return false;
    try {
      const { data } = await claimReferralFn({ code: referralCodeUsed });
      await loadMarketingData();
      return Boolean(data?.ok);
    } catch (error) {
      console.error('[rewards] claimReferral', error);
      return false;
    }
  };

  const addPoints = async (amountOrReason, reasonMaybe, descriptionMaybe, options = {}) => {
    if (!currentUser) return 0;

    let reason = 'signup';
    let description = '';

    if (typeof amountOrReason === 'number' && typeof reasonMaybe === 'string') {
      reason = reasonMaybe;
      description = typeof descriptionMaybe === 'string' ? descriptionMaybe : '';
    } else if (typeof amountOrReason === 'string') {
      reason = amountOrReason;
      description = typeof reasonMaybe === 'string' ? reasonMaybe : '';
    }

    const normalized = String(reason || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_');

    const map = {
      signup: 'signup',
      welcome: 'signup',
      first_booking: 'first_booking',
      review: 'review',
      complete_profile: 'complete_profile'
    };

    const action = map[normalized];
    if (!action) {
      console.warn('[rewards] addPoints ignored reason', reason);
      return userPoints;
    }

    try {
      const { data } = await awardPointsFn({
        reason: action,
        description,
        reviewId: options.reviewId || null,
        idempotencyKey: options.idempotencyKey || null
      });
      if (data?.unlockedBadges?.length) {
        const label = localizeBadge(data.unlockedBadges[0], 'fr');
        toast.success(`Nouveau statut : ${label}`);
      }
      await loadMarketingData();
      return data?.points ?? userPoints;
    } catch (error) {
      console.error('[rewards] awardPoints', error);
      return userPoints;
    }
  };

  const getAvailableRewards = (language = 'fr') =>
    REWARD_CATALOG.map((r) => localizeReward(r, language));

  const redeemReward = async (rewardId) => {
    if (!currentUser) return false;
    try {
      const { data } = await redeemRewardFn({ rewardId });
      await loadMarketingData();
      toast.success('Récompense obtenue');
      return Boolean(data?.ok);
    } catch (error) {
      const msg = String(error?.message || '')
        .replace(/^Firebase:\s*/i, '')
        .replace(/\s*\([^)]*\)\s*$/, '');
      toast.error(msg || 'Échange impossible');
      return false;
    }
  };

  const value = {
    userPoints,
    userBadges,
    referralCode,
    referrals,
    pointsHistory,
    coupons,
    loading,
    marketingStats,
    addPoints,
    registerReferral,
    redeemReward,
    getAvailableRewards,
    refreshRewards: loadMarketingData,
    POINTS_ACTIONS,
    localizeBadge
  };

  return (
    <MarketingContext.Provider value={value}>
      {children}
    </MarketingContext.Provider>
  );
};
